import { Alert, Icons, SolvTheme, Stack, Text } from '@solvhealth/jigsaw-v1';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useIsMember } from '~/components/Membership/hooks/useIsMember';
import useShouldShowPremiumInPersonEntrypoint from '~/components/Membership/hooks/useShouldShowPremiumInPersonEntrypoint';
import {
  PREMIUM_IN_PERSON_CTA_TYPE_LEARN_MORE,
  PREMIUM_IN_PERSON_CTA_TYPE_SIGN_UP,
  PREMIUM_IN_PERSON_ENTRYPOINT_TAPPED,
} from '~/core/analytics/events';
import PremiumInPersonEntrypoint from './PremiumInPersonEntrypoint';
import SolvPremiumEntrypoint from './SolvPremiumEntrypoint';
import { useMostConvenientInPersonPremiumLocation } from '~/components/AccountGetsMarried/components/GetCare/components/NavTiles/hooks/useMostConvenientInPersonPremiumLocation';

// Make sure a little bit of the next card shows as an affordance for scroll behavior
const ConstrainedCard = styled(Stack).withConfig({ componentId: 'sc-j757hf-0' })({
  '--scroll-affordance': '30px',
  '--x-padding': SolvTheme.space[3],
  maxWidth: `min(calc(100vw - var(--x-padding) - var(--gap) - var(--scroll-affordance)), 375px)`,
  flexShrink: 0,

  '&:last-of-type': {
    '--scroll-affordance': '0px',
  },
});

const InPersonNonMemberLearnMore = ({ show }: { show: boolean }) => {
  const { t } = useTranslation(['account', 'common']);

  return (
    <Alert
      actions={[
        <Alert.Action
          event={PREMIUM_IN_PERSON_ENTRYPOINT_TAPPED}
          href="/membership/payment"
          key={0}
          meta={{
            isExistingSolvPlusMember: false,
            ctaType: PREMIUM_IN_PERSON_CTA_TYPE_SIGN_UP,
          }}
        >
          {t('common:signUpNow')}
        </Alert.Action>,
        <Alert.Action
          event={PREMIUM_IN_PERSON_ENTRYPOINT_TAPPED}
          href="/membership/in-person"
          key={1}
          meta={{
            isExistingSolvPlusMember: false,
            ctaType: PREMIUM_IN_PERSON_CTA_TYPE_LEARN_MORE,
          }}
        >
          {t('common:learnMore')}
        </Alert.Action>,
      ]}
      collapsible
      icon={<img height={24} src="/images/membership/ThickPlusIcon.svg" width={24} />}
      show={show}
      title={t('account:inPersonPremium.upsellAlert.title')}
      tone={{
        custom: {
          background: 'teal-xlight',
          muted: 'teal-medium',
          text: 'teal-dark',
        },
      }}
    >
      {t('account:inPersonPremium.upsellAlert.subtitle')}
    </Alert>
  );
};

const InPersonMemberLearnMore = ({ show }: { show: boolean }) => {
  const { t } = useTranslation(['account', 'common']);

  return (
    <Alert
      actions={[
        <Alert.Action
          event={PREMIUM_IN_PERSON_ENTRYPOINT_TAPPED}
          href="/membership/in-person"
          key={0}
          meta={{
            isExistingSolvPlusMember: true,
            ctaType: PREMIUM_IN_PERSON_CTA_TYPE_LEARN_MORE,
          }}
        >
          {t('common:learnMore')}
        </Alert.Action>,
      ]}
      collapsible
      icon={<Icons.BellRing />}
      show={show}
      title={t('account:inPersonPremium.memberAlert.title')}
      tone={{
        custom: {
          background: 'teal-xlight',
          muted: 'teal-medium',
          text: 'teal-dark',
        },
      }}
    >
      {t('account:inPersonPremium.memberAlert.subtitle')}
    </Alert>
  );
};

export const PremiumVisitSection = ({ userProfileId }: { userProfileId: string }) => {
  const { t } = useTranslation(['account', 'common']);
  const isMember = useIsMember();
  const { loading } = useMostConvenientInPersonPremiumLocation();

  const {
    shouldShowBookNow: shouldShowInPersonBookNow,
    shouldShowMemberLearnMore: shouldShowInPersonMemberLearnMore,
    shouldShowNonMemberLearnMore: shouldShowInPersonNonMemberLearnMore,
  } = useShouldShowPremiumInPersonEntrypoint();

  return (
    <Stack alignItems="flex-start" space={2}>
      <Stack alignItems="flex-start" px={3} space={2}>
        <Text variant="h3">
          {t(
            isMember
              ? 'account:premiumVisitSection.memberTitle'
              : 'account:premiumVisitSection.title'
          )}
        </Text>
      </Stack>

      <Stack alignItems="flex-start" px={3}>
        <InPersonNonMemberLearnMore show={shouldShowInPersonNonMemberLearnMore} />
        <InPersonMemberLearnMore show={shouldShowInPersonMemberLearnMore} />
      </Stack>

      <Stack
        alignItems="stretch"
        display={{ _: 'flex', md: 'grid' }}
        overflowX="scroll"
        pb={1}
        pt={1}
        px={3}
        row
        style={{
          // Jigsaw doesn't like CSS variables (yet)
          // @ts-ignore
          '--gap': `${SolvTheme.space[2.5]}`,
          gap: 'var(--gap)',
          gridTemplateColumns: 'repeat(2, 1fr)',
        }}
      >
        {shouldShowInPersonBookNow && !loading && (
          <ConstrainedCard>
            <PremiumInPersonEntrypoint userProfileId={userProfileId} />
          </ConstrainedCard>
        )}
        <ConstrainedCard>
          <SolvPremiumEntrypoint userProfileId={userProfileId} />
        </ConstrainedCard>
      </Stack>
    </Stack>
  );
};
